import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { usePreloadAssets } from '../../../../hooks/UsePreloadAssets';
import { FPAItem } from '../../../../infra/protected/FPA/FPAItem';
import { FPAData, FPADataTypes } from '../../../../infra/protected/FPA/FPAData';
import { useDependency } from '../../../../contexts/DependencyProvider';
import { Col, FilterCol, FilterFolder, FilterProject, ListFPARequest, Order, Parent } from '../../../../services/soap/project/requests/ListFPARequest';
import { MoveActivityRequest } from '../../../../services/rest/steward/requests/MoveActivityRequest';
import { MoveProjectRequest } from '../../../../services/rest/steward/requests/MoveProjectRequest';

function addChildrenUnderParent(items: FPAItem[], parent: number, newChildren: FPAItem[]): FPAItem[] {
    return items.map((item: FPAItem) => {
        if (+item.id === +parent) {
            return { ...item, childItems: newChildren };
        }
        if (item.childItems.length > 0) {
            return { ...item, childItems: addChildrenUnderParent(item.childItems, parent, newChildren) };
        }
        return item;
    });
}
export interface IUseFinderContextTreeProps {
    parentItem: FPAData | null;
    showFolders: boolean;
    showProjects: boolean;
    showActivities: boolean;
    additionalFilter?: { relatedToActivityType?: number, fpaTypeCategories?: string };
}

export interface ProcessMoveResult {
    moved: boolean;
    error: string;
    itemId: number;
}

export function useFinderContextTree ({
    parentItem,
    showFolders,
    showProjects,
    showActivities,
    additionalFilter,
}: IUseFinderContextTreeProps) {
    const [treeData, setTreeData] = useState<FPAItem[]>([]);
    const [loading, setLoading] = useState(false);
    const { folderTypes } = usePreloadAssets();
    const { store, projectService, stewardRestService } = useDependency();
    const children = useRef<{[key:string]: any}>({});
    const item_types = useRef<{[key:number]: any}>({});
    const cancelSource = useRef<any>(null);
    const treeDataRef = useRef<FPAItem[]>([]);

    const { relatedToActivityType, fpaTypeCategories } = additionalFilter ?? {};

    /** Must run before paint so async revealPath/updateData never reads a stale [] while `treeData` already has roots. */
    useLayoutEffect(() => {
        treeDataRef.current = treeData;
    }, [treeData]);

    const loadItems = useCallback( async (key:number, filterText?:string) : Promise<FPAItem[]> => {
        var folderFilter = new FilterFolder();
        var projectFilter = new FilterProject();

        var parentContext = undefined;

        switch(item_types.current[key]) {
            case FPADataTypes.FOLDER_TYPE:
                folderFilter.COL.push(new FilterCol('folderType', 'equals', ""+key));
                if(fpaTypeCategories) {
                    folderFilter.COL.push(new FilterCol('fpaTypeCategories', 'any', fpaTypeCategories));
                }
                break;
            case FPADataTypes.FOLDER:
                parentContext = new Parent(key, 'FOLDER');
                if(relatedToActivityType) { 
                    projectFilter.COL.push(new FilterCol('relatedToActivityType', 'equals', ""+relatedToActivityType));
                }
                if(fpaTypeCategories) {
                    folderFilter.COL.push(new FilterCol('fpaTypeCategories', 'any', fpaTypeCategories));
                }
                break;
            case FPADataTypes.PROJECT:
                parentContext = new Parent(key, 'PROJECT');
                break;
            case FPADataTypes.ACTIVITY:
                parentContext = new Parent(key, 'ACTIVITY');
                break;
            default:
                console.log(`selected unknown type: ${key}`);
        }
        
        if(cancelSource.current) {
            cancelSource.current.cancel();
        }

        const request = new ListFPARequest(store.Server, store.SessionId, 
            filterText,
            false, 
            50, 
            0,
            undefined,
            false,
            undefined,
            parentContext,
            folderFilter,
            projectFilter, 
            undefined,
            new Order([
              new Col('itemTypeName'), 
              new Col('name'), 
              new Col('created', true)
            ])
        );

        cancelSource.current = request.cancelSource;
        setLoading(true);
        const response = await projectService.listFPA(request);
        var new_items:FPAItem[] = [];
        if(response && response.count > 0) {
            new_items = response.ITEMS.map((item:any) => {
                var type = FPADataTypes.ITEM_TYPE;
                var id = null;
                var name = null;

                if(item.ITEM.FOLDER){
                    if(!showFolders) {
                        return  { id: 0, title: '', type: FPADataTypes.NONE, childItems: [] };
                    }
                    id = item.ITEM.FOLDER.id;
                    name = item.ITEM.FOLDER.name;
                    type = FPADataTypes.FOLDER;
                }
                else if(item.ITEM.PROJECT){
                    if(!showProjects) {
                        return  { id: 0, title: '', type: FPADataTypes.NONE, childItems: [] };
                    }
                    id = item.ITEM.PROJECT.id;
                    name = item.ITEM.PROJECT.name;
                    type = FPADataTypes.PROJECT;
                }
                else if(item.ITEM.ACTIVITY){
                    if(!showActivities) {
                        return  { id: 0, title: '', type: FPADataTypes.NONE, childItems: [] };
                    }
                    id = item.ITEM.ACTIVITY.id;
                    name = item.ITEM.ACTIVITY.name;
                    type = FPADataTypes.ACTIVITY;
                }

                item_types.current = { ...item_types.current , [id]:type };
                // children.current = { ...children.current, [id]:true };
                return{
                    id: id, 
                    title: name, 
                    type: type, 
                    childItems: [] 
                };
            }).filter((item:FPAItem) => item.type !== FPADataTypes.NONE);
            
        }
        setLoading(false);
        return new_items;
    }, [store, projectService, relatedToActivityType, fpaTypeCategories, showFolders, showProjects, showActivities]);

    const updateData = useCallback(async (key: number, filterText?: string) => {
        let modified_key: string = key.toString();
        if (filterText) {
            modified_key = `${key}_${filterText}`;
        }
        const new_items = children.current[modified_key]
            ? children.current[modified_key]
            : await loadItems(key, filterText ? filterText + '*' : undefined);
        children.current[modified_key] = new_items;
        const base = JSON.parse(JSON.stringify(treeDataRef.current)) as FPAItem[];
        const updatedTreeData = addChildrenUnderParent(base, key, new_items);
        treeDataRef.current = updatedTreeData;
        setTreeData(JSON.parse(JSON.stringify(updatedTreeData)));
    }, [loadItems]);

    const onSelection = useCallback(async (key: number) => {
        await updateData(key);
    }, [updateData]);

    const revealPath = useCallback(
        async (ids: number[]) => {
            for (const id of ids) {
                await updateData(id);
            }
        },
        [updateData]
    );

    useEffect(() => {
        var new_items:FPAItem[] = [];

        if(parentItem){
            item_types.current = { ...item_types.current , [parentItem.id]:parentItem.type };
            loadItems(parentItem.id).then((items) => {
                new_items = [{ id: parentItem.id, title: parentItem.title, type: parentItem.type, childItems: items }];
                treeDataRef.current = new_items;
                setTreeData(new_items);
            });
        }
    }, [parentItem, loadItems]);  
    
    useEffect(() => {
        if (!parentItem && folderTypes) {
            var new_items = Object.keys(folderTypes).map((ft:any) => (
                { 
                    id: folderTypes[ft].id, 
                    title: folderTypes[ft].name, 
                    type: FPADataTypes.FOLDER_TYPE, 
                    childItems: [] 
                })).sort((a, b) => a.title.localeCompare(b.title));
            const folderTypeRec = folderTypes as unknown as Record<string, { id: number; name: string }>;
            for (const ft of Object.keys(folderTypeRec)) {
                const fid = folderTypeRec[ft].id;
                item_types.current = { ...item_types.current, [fid]: FPADataTypes.FOLDER_TYPE };
            }

            treeDataRef.current = new_items;
            setTreeData(new_items);
        }
    }, [folderTypes, parentItem]);

    const processMove = async (moveType: FPADataTypes, sourceItemId:string, destinationItemId: string): Promise<ProcessMoveResult> => {
        var moved = false;
        var error = '';
        var itemId = 0;
        setLoading(true);
        switch(moveType) {
            case FPADataTypes.FOLDER:
                console.log('moving folder: ', sourceItemId);
                error = 'Not implemented';
                break;
            case FPADataTypes.PROJECT:
                var moveProjectResponse = await stewardRestService.moveProject(new MoveProjectRequest(store.Server, store.SessionId, `${sourceItemId}`, `${destinationItemId}`));
                moved = moveProjectResponse.result === 'SUCCESS';
                if(moved) {
                    itemId = moveProjectResponse.new_project_id;
                }
                break;
            case FPADataTypes.ACTIVITY:
                var moveActivityResponse = await stewardRestService.moveActivity(new MoveActivityRequest(store.Server, store.SessionId, `${sourceItemId}`, `${destinationItemId}`));
                moved = moveActivityResponse.result === 'SUCCESS';
                if(moved) {
                    itemId = moveActivityResponse.new_activity_id;
                }
                break;
            default:
                console.log('unknown type: ', sourceItemId);
                error = 'Unknown type';
        }
        setLoading(false);
        return { moved, error, itemId };
    }

    const onSearch = (value: string, contextItem: FPAItem | null) => {
        if(contextItem) {
            updateData(contextItem.id, value);
        }
    }

    return { treeData, onSelection, loading, processMove, onSearch, revealPath };
}
